import React, { useState, useEffect } from 'react';
import { Ticket, TicketCategory, TicketSite, TicketPriority } from '../../../entities/ticket';
import { getTicketCategories } from '../../../entities/ticket/api/ticketApi';
import { CreateTicketUseCase } from '../application';
import * as S from './CreateTicketModal.styles';

interface CreateTicketModalProps {
  isOpen: boolean;
  onClose: () => void;
  onTicketCreated: (ticket: Ticket) => void;
}

export const CreateTicketModal: React.FC<CreateTicketModalProps> = ({
  isOpen,
  onClose,
  onTicketCreated,
}) => {
  const [title, setTitle] = useState('');
  const [site, setSite] = useState<TicketSite>('hudle');
  const [clientEmail, setClientEmail] = useState('');
  const [priority, setPriority] = useState<TicketPriority>('medium');
  const [category, setCategory] = useState('');
  const [initialMessage, setInitialMessage] = useState('');
  const [categories, setCategories] = useState<TicketCategory[]>([]);
  const [creating, setCreating] = useState(false);

  const createTicketUseCase = new CreateTicketUseCase();

  const loadCategories = async () => {
    try {
      const data = await getTicketCategories(site);
      setCategories(data);
    } catch (error) {
      console.error('Ошибка загрузки категорий:', error);
    }
  };
  
  useEffect(() => {
    if (isOpen) {
      loadCategories();
    }
  }, [isOpen, site]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!title.trim() || !clientEmail.trim()) {
      alert('Заполните обязательные поля');
      return;
    }

    try {
      setCreating(true);
      const ticket = await createTicketUseCase.execute({
        title: title.trim(),
        site,
        clientEmail: clientEmail.trim(),
        priority,
        category: category || undefined,
        initialMessage: initialMessage.trim() || undefined,
      });
      
      onTicketCreated(ticket);
      handleClose();
    } catch (error) {
      console.error('Ошибка создания тикета:', error);
      alert('Не удалось создать тикет');
    } finally {
      setCreating(false);
    }
  };

  const handleClose = () => {
    setTitle('');
    setSite('hudle');
    setClientEmail('');
    setPriority('medium');
    setCategory('');
    setInitialMessage('');
    onClose();
  };

  if (!isOpen) return null;

  return (
    <S.ModalOverlay onClick={handleClose}>
      <S.ModalContent onClick={(e) => e.stopPropagation()}>
        <S.ModalHeader>
          <S.ModalTitle>Создать тикет</S.ModalTitle>
          <S.CloseButton onClick={handleClose}>×</S.CloseButton>
        </S.ModalHeader>

        <form onSubmit={handleSubmit}>
          <S.ModalBody>
            <S.FormGroup>
              <S.Label>Название тикета *</S.Label>
              <S.Input
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Например: Yandex Market - Team Alpha"
                required
              />
            </S.FormGroup>

            <S.FormGroup>
              <S.Label>Сайт *</S.Label>
              <S.Select value={site} onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setSite(e.target.value as TicketSite)} required>
                <option value="hudle">Hudle</option>
                <option value="tournament">Турнирный сайт</option>
              </S.Select>
            </S.FormGroup>

            <S.FormGroup>
              <S.Label>Почта *</S.Label>
              <S.Input
                type="email"
                value={clientEmail}
                onChange={(e) => setClientEmail(e.target.value)}
                placeholder="email@example.com"
                required
              />
            </S.FormGroup>

            <S.FormGroup>
              <S.Label>Приоритет</S.Label>
              <S.Select value={priority} onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setPriority(e.target.value as TicketPriority)}>
                <option value="low">Низкий</option>
                <option value="medium">Средний</option>
                <option value="high">Высокий</option>
                <option value="urgent">Срочный</option>
              </S.Select>
            </S.FormGroup>

            <S.FormGroup>
              <S.Label>Категория</S.Label>
              <S.Select value={category} onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setCategory(e.target.value)}>
                <option value="">Выберите категорию</option>
                {categories.map(cat => (
                  <option key={cat.id} value={cat.id}>
                    {cat.name}
                  </option>
                ))}
              </S.Select>
            </S.FormGroup>

            <S.FormGroup>
              <S.Label>Первое сообщение</S.Label>
              <S.Textarea
                value={initialMessage}
                onChange={(e) => setInitialMessage(e.target.value)}
                placeholder="Напишите первое сообщение от клиента..."
              />
            </S.FormGroup>
          </S.ModalBody>

          <S.ModalFooter>
            <S.Button type="button" onClick={handleClose}>
              Отмена
            </S.Button>
            <S.Button type="submit" $variant="primary" disabled={creating}>
              {creating ? 'Создание...' : 'Создать'}
            </S.Button>
          </S.ModalFooter>
        </form>
      </S.ModalContent>
    </S.ModalOverlay>
  );
};

